import { useEffect, useState } from "react";
import { RefreshCw, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EXTENSION_MODE_LABELS } from "@/components/ExtendEndCard";

const STATUS = {
  active_link_issued: { label: "Live · awaiting payment", color: "#FFB020" },
  awaiting_payment: { label: "Awaiting payment", color: "#FFB020" },
  active_paid: { label: "Paid", color: "#CCFF00" },
  active_complimentary: { label: "Complimentary", color: "#66C2FF" },
  cancelled: { label: "Cancelled", color: "#8A8A8A" },
  link_cancelled: { label: "Live · link cancelled", color: "#8A8A8A" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export function ExtensionsList({ festivalId, reloadToken, onChanged }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/festival/${festivalId}/toolkit/extensions`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [festivalId, reloadToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (ext, action) => {
    setBusy(ext.id + action);
    try {
      await api.post(`/festival/${festivalId}/toolkit/extensions/${ext.id}/${action}`, action === "resend" ? { origin_url: window.location.origin } : {});
      toast.success(action === "resend" ? "New payment link emailed" : ext.status === "active_link_issued" ? "Payment link cancelled — reminders stopped" : "Extension cancelled");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e, "Failed")); } finally { setBusy(null); }
  };

  if (!items.length) return null;
  return (
    <div className="card-surface p-4 mb-6" data-testid="toolkit-extensions-list">
      <div className="label-mono text-[10px] text-[#CCFF00] mb-3">EXTENSIONS</div>
      <div className="space-y-2">
        {items.map((e) => {
          const st = STATUS[e.status] || { label: e.status, color: "#A3A3A3" };
          const unpaid = e.status === "awaiting_payment" || e.status === "active_link_issued";
          return (
            <div key={e.id} className="border border-[#262626] p-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`toolkit-extension-${e.id}`}>
              <div className="min-w-0">
                <div className="text-xs text-white font-semibold">{EXTENSION_MODE_LABELS[e.mode]}{e.amount ? ` · £${Number(e.amount).toFixed(2)}` : ""}</div>
                <div className="text-[11px] text-[#A3A3A3]">New end {fmt(e.new_end_at)} · set {fmt(e.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest border px-2 py-0.5" style={{ color: st.color, borderColor: st.color }} data-testid={`toolkit-extension-status-${e.id}`}>{st.label}</span>
                {e.status === "active_link_issued" && (
                  <button type="button" disabled={!!busy} onClick={() => act(e, "resend")} data-testid={`toolkit-extension-resend-${e.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white disabled:opacity-40">
                    <RefreshCw className="w-3 h-3" /> Resend link
                  </button>
                )}
                {unpaid && (
                  <button type="button" disabled={!!busy} onClick={() => act(e, "cancel")} data-testid={`toolkit-extension-cancel-${e.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black disabled:opacity-40">
                    <XCircle className="w-3 h-3" /> {e.status === "active_link_issued" ? "Cancel link" : "Cancel"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
